/**
 * Module dependencies.
 */

import { User } from 'app/types/user';
import React, { useMemo } from 'react';
import styles from './user-detail.module.less';
import { Field } from '../field/field';
import { Title } from 'app/components/atoms/title/title';
import { useTranslation } from 'react-i18next';
import { useGetExperimentalFeatures } from 'app/hooks/app-requests/user/use-get-experimental-features';
import { Checkbox } from 'app/components/atoms/forms/fields/checkbox/checkbox';
import { usePostUserExperimentalFeature } from 'app/hooks/app-requests/user/use-post-change-experimental-feature';
import { cookieStorage } from 'app/core/utils/cookie-storage';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import { SendNotificationButton } from 'app/components/molecules/send-notification-button/send-notification-button';

/**
 * `Props` type.
 */

type Props = {
  user: User | undefined;
  refetch: () => Promise<void>;
};

/**
 * Export `UserDetail` component.
 */

export function UserDetail({ user, refetch }: Props): JSX.Element | null {
  const [translate] = useTranslation();
  const { data } = useGetExperimentalFeatures();
  const { mutateAsync: changeExperimentalFeature } = usePostUserExperimentalFeature();
  const session = useMemo(() => {
    return cookieStorage.get('user');
  }, []);

  const isMainDeveloper = session.email === 'francisco.morais@terah.pt';

  if (!user) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <Title>{user.name}</Title>

      <div className={styles.content}>
        <div className={styles.column}>
          <Title level={'h2'}>{translate('users.userInfo.userInfo.title')}</Title>

          <Field title={translate('common.labels.id')} value={user.id} />

          <Field title={translate('common.labels.name')} value={user.name} />

          <Field title={translate('common.labels.email')} value={user.email} />

          <Field title={translate('common.labels.phoneNumber')} value={user.phoneNumber} />

          <Field title={translate('common.labels.accessType')} value={user.accessType} />

          <Field title={translate('common.labels.language')} value={user.language} />

          <Field last={!isMainDeveloper} title={translate('common.labels.gender')} value={user.gender} />

          {isMainDeveloper && <Field last title={translate('common.labels.pinCode')} value={(user as any).authPin} />}
        </div>

        <div className={styles.column}>
          <Title level={'h2'}>{translate('users.userInfo.address.title')}</Title>

          <Field title={translate('common.labels.address')} value={user.address?.address} />

          <Field title={translate('common.labels.postalCode')} value={user.postalCode} />

          <Field title={translate('common.labels.city')} value={user.city} />

          <Field title={translate('common.labels.countryCode')} value={user.countryCode} />

          <Field title={translate('common.labels.nationality')} value={user.nationality} />

          <Field last title={translate('common.labels.birthDate')} value={user.birthDate} />
        </div>

        <div className={styles.column}>
          <Title level={'h2'}>{translate('users.userInfo.status.title')}</Title>

          <Field title={translate('common.labels.confirmed')} value={translate(`common.boolean.${!!user.confirmed}`)} />

          <Field title={translate('common.labels.blocked')} value={translate(`common.boolean.${!!user.blocked}`)} />

          <Field
            title={translate('common.labels.guestAccount')}
            value={translate(`common.boolean.${!!user.guestAccount}`)}
          />

          <Field title={translate('common.labels.uninstall')} value={translate(`common.boolean.${!!user.uninstall}`)} />

          <Field title={translate('common.labels.createdAt')} value={<DateTime date={user.createdAt} />} />

          <Field last title={translate('common.labels.updatedAt')} value={<DateTime date={user.updatedAt} />} />
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.column}>
          <Title level={'h2'}>{translate('users.userInfo.experimentalFeatures.title')}</Title>

          {data?.map(item => (
            <Checkbox
              disabled={item.enabled}
              error={null}
              key={item.code}
              label={item.name}
              onChange={async value => {
                await changeExperimentalFeature({
                  enabled: value.target.checked,
                  featureCode: item.code,
                  userId: user.id
                });

                await refetch();
              }}
              value={
                item.enabled ||
                user.experimentalFeatures.find(feature => {
                  return feature.code === item.code;
                })
              }
            />
          ))}
        </div>

        <div className={styles.column}>
          <Title level={'h2'}>{translate('users.userInfo.debugger.title')}</Title>

          <SendNotificationButton />
        </div>
      </div>
    </div>
  );
}
